import {
  useCallback,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent
} from "react";

import type { ActivityExecutionContext } from "@/app/integration/contracts";
import {
  useCurriculumFeedbackAudio,
  useCurriculumVoiceSequence
} from "@/features/audio";
import { AudioReplayButton, CurriculumVoiceButton } from "@/ui";

import {
  HabitsFeedbackNotice,
  HabitsInstructionHeader,
  HabitsResult,
  HabitsSceneImage
} from "./PlayerChrome";
import type { SchoolScenarioPlayerProps } from "./player-contracts";
import { useCompletionReporting, usePlayerFocus } from "./player-lifecycle";
import type { SchoolScenarioAction } from "./types";

export function SchoolScenarioPlayer({
  audioPlayer,
  binding,
  onAudioReplay,
  onComplete,
  onReplay,
  reducedMotion
}: SchoolScenarioPlayerProps) {
  const titleId = useId();
  const instructionId = useId();
  const sceneTitleId = useId();
  const choicesTitleId = useId();
  const context = useMemo<ActivityExecutionContext>(
    () => ({
      nowMs: Date.now,
      reducedMotion
    }),
    [reducedMotion]
  );
  const payload = binding.level.payload;
  const [state, setState] = useState(() =>
    binding.adapter.createInitialState(payload, context)
  );
  const firstControlRef = useRef<HTMLButtonElement>(null);
  const resultHeadingRef = useRef<HTMLHeadingElement>(null);

  const dispatch = useCallback(
    (action: SchoolScenarioAction) => {
      setState((currentState) =>
        binding.adapter.reduce(currentState, payload, action, context)
      );
    },
    [binding, context, payload]
  );

  const model = useMemo(
    () => binding.adapter.toRenderModel(state, payload),
    [binding, payload, state]
  );
  const metrics = useMemo(
    () => binding.adapter.getMetrics(state, payload),
    [binding, payload, state]
  );
  const sceneAudioPath = model.sceneNarration.audioPath;
  const feedback = model.feedback;
  const followUp = model.followUp;

  const feedbackAudio = useCurriculumFeedbackAudio(
    audioPlayer,
    feedback,
    false
  );
  useCurriculumVoiceSequence(audioPlayer, [
    feedback,
    model.phase === "result" ? followUp : null
  ]);

  useCompletionReporting(metrics, onComplete);
  usePlayerFocus(metrics.completed, firstControlRef, resultHeadingRef);

  const choose = useCallback(
    (choiceId: string, inputMethod: "keyboard" | "pointer") => {
      dispatch({
        type: "choose",
        choiceId,
        inputMethod
      });
    },
    [dispatch]
  );

  const handleChoiceKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>, choiceId: string) => {
      if (event.key !== "Enter" && event.key !== " ") {
        return;
      }

      event.preventDefault();
      choose(choiceId, "keyboard");
    },
    [choose]
  );

  const handleReplay = useCallback(() => {
    dispatch({ type: "replay" });
    onReplay?.(binding.level.id);
  }, [binding.level.id, dispatch, onReplay]);

  return (
    <section
      aria-labelledby={titleId}
      className="habits-player habits-player--scenario"
      data-activity-type={binding.activityType}
      data-reduced-motion={reducedMotion}
    >
      <HabitsInstructionHeader
        difficulty={model.difficulty}
        instruction={model.instruction}
        instructionId={instructionId}
        instructionPlaying={audioPlayer.isPlayingPath(
          model.instruction.audioPath
        )}
        levelId={model.levelId}
        onAudioReplay={onAudioReplay}
        title={model.title}
        titleId={titleId}
      />
      <HabitsSceneImage src={model.sceneImagePath} />

      <section
        aria-labelledby={sceneTitleId}
        className="habits-scenario__narration"
      >
        <div>
          <h3 id={sceneTitleId}>发生了什么</h3>
          <p>{model.sceneNarration.caption}</p>
        </div>
        <AudioReplayButton
          isPlaying={audioPlayer.isPlayingPath(sceneAudioPath)}
          label="再听情境"
          onClick={() =>
            onAudioReplay({
              audioPath: sceneAudioPath,
              caption: model.sceneNarration.caption,
              kind: "scene-narration",
              levelId: model.levelId
            })
          }
          playingLabel="正在播放情境"
        />
      </section>

      {model.phase === "result" && model.result !== null ? (
        <div className="habits-scenario__result-wrap">
          <HabitsResult
            description={feedback?.text ?? "这个办法把安全和需要都照顾到了。"}
            descriptionVoice={
              feedback === null
                ? undefined
                : {
                    isPlaying: feedbackAudio.isPlaying,
                    label: feedback.text,
                    onPlay: () =>
                      onAudioReplay({
                        audioPath: feedback.audioPath,
                        caption: feedback.text,
                        kind: "feedback",
                        levelId: model.levelId
                      })
                  }
            }
            metrics={model.result}
            onReplay={handleReplay}
            resultHeadingRef={resultHeadingRef}
          />
          {followUp === null ? null : (
            <div className="habits-scenario__follow-up-row">
              <p className="habits-scenario__follow-up">{followUp.text}</p>
              <CurriculumVoiceButton
                isPlaying={audioPlayer.isPlayingPath(followUp.audioPath)}
                label={followUp.text}
                onPlay={() =>
                  onAudioReplay({
                    audioPath: followUp.audioPath,
                    caption: followUp.text,
                    kind: "follow-up",
                    levelId: model.levelId
                  })
                }
              />
            </div>
          )}
        </div>
      ) : (
        <>
          <section
            aria-describedby={instructionId + " " + sceneTitleId}
            aria-labelledby={choicesTitleId}
            className="habits-player__work-area"
          >
            <h3 id={choicesTitleId}>选一个更合适的做法</h3>
            <div className="habits-scenario__choices">
              {model.choices.map((choice, index) => (
                <div className="habits-scenario__choice-wrap" key={choice.id}>
                  <button
                    aria-label={"选择：" + choice.text}
                    aria-pressed={choice.selected}
                    className="habits-scenario__choice"
                    onClick={() => choose(choice.id, "pointer")}
                    onKeyDown={(event) => handleChoiceKeyDown(event, choice.id)}
                    ref={index === 0 ? firstControlRef : undefined}
                    type="button"
                  >
                    <span
                      aria-hidden="true"
                      className="habits-scenario__choice-number"
                    >
                      {index + 1}
                    </span>
                    <span>{choice.text}</span>
                  </button>
                  <CurriculumVoiceButton
                    isPlaying={audioPlayer.isPlayingPath(choice.audioPath)}
                    label={choice.text}
                    onPlay={() =>
                      onAudioReplay({
                        audioPath: choice.audioPath,
                        caption: choice.text,
                        kind: "choice",
                        levelId: model.levelId
                      })
                    }
                  />
                </div>
              ))}
            </div>
          </section>
          <HabitsFeedbackNotice
            feedback={feedback}
            isPlaying={feedbackAudio.isPlaying}
            onReplay={() => {
              if (feedback !== null) {
                onAudioReplay({
                  audioPath: feedback.audioPath,
                  caption: feedback.text,
                  kind: "feedback",
                  levelId: model.levelId
                });
              }
            }}
          />
        </>
      )}
    </section>
  );
}
